import { useCallback, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation, useSubscription } from '@apollo/client';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { InformationTip } from '@bloomays-lib/ui.shared';
import { StatusChip, RowActionsMenu, AvatarCell, avatarToneFromSeed } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2RowAction, DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';
import {
  TemplateContract,
  type ResolvedContact,
  type CancelLegalContractSignatureGQLResult,
  type RelaunchLegalContractSignatureGQLResult,
} from '@bloomays-lib/types.shared';
import {
  GET_LEGAL_CONTRACTS_BY_MISSION,
  RELAUNCH_LEGAL_CONTRACT_SIGNATURE,
  CANCEL_LEGAL_CONTRACT_SIGNATURE,
  JOBS_COMPLETED_SUBSCRIPTION,
  JOBS_ERRORED_SUBSCRIPTION,
} from '@bloomays-lib/adapter.api-bloomer';
import { dateFormat, initialsFromName, isContractSigned } from '@bloomays-lib/utils.shared';
import {
  isManualRelaunchOnCooldown,
  resolveCancellationAuthorLabel,
  resolveSignerDisplayName,
  type DocumentVolet,
} from '../../helpers/legalContracts';
import { notify } from '../../helpers/toastify';
import LegalContractFile from './LegalContractFile';
import ContractTypeBadge from './ContractTypeBadge';
import CancelLegalContractSignatureModal, {
  type CancelLegalContractSignaturePayload,
} from './CancelLegalContractSignatureModal';

export type DocumentVoletKind = TemplateContract.SST | TemplateContract.PDS;

export type DocumentVoletCardProps = {
  volet: DocumentVolet | null;
  voletKind: DocumentVoletKind;
  missionRecordId: string;
  /** Signer for this volet (not the bloomer); reused by the upload modal. */
  resolvedSigner: ResolvedContact | null;
  onClickPreview: (file: string) => void;
  onClickDownload: (file: string) => void;
  setUploadingFile: (value: boolean) => void;
};

type UploadTarget = 'original' | 'signed';

/** Fallback refetch if the job subscription never fires (race / dropped websocket). */
const CANCELLATION_FEEDBACK_TIMEOUT_MS = 20_000;

const extractServerErrorMessage = (error: unknown): string | undefined =>
  (error as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message;

/**
 * A single volet (SST or PDS) card within a documentary line (AC2). Shows the
 * type badge, status, resolved signer, dates and file actions for that volet, or an
 * explicit "non concerné" state when the mission's document line has no
 * contract for this volet. Upload / view / download all live in the contextual
 * ⋮ menu (paperclip triggers are hidden).
 */
const DocumentVoletCard = ({
  volet,
  voletKind,
  missionRecordId,
  resolvedSigner,
  onClickPreview,
  onClickDownload,
  setUploadingFile,
}: DocumentVoletCardProps) => {
  const { t } = useTranslation(['legalContracts']);
  const theme = useTheme();
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);
  const apolloClient = useApolloClient();
  const refetchAfterAction = {
    refetchQueries: [
      { query: GET_LEGAL_CONTRACTS_BY_MISSION, variables: { missionId: missionRecordId } },
      'getLegalContractsWithSummary',
    ],
  };
  const [relaunchSignature, { loading: relaunching }] = useMutation<RelaunchLegalContractSignatureGQLResult>(
    RELAUNCH_LEGAL_CONTRACT_SIGNATURE,
    refetchAfterAction,
  );
  // Refetch on job completion (mutation only queues).
  const [cancelSignature, { loading: submittingCancel }] = useMutation<CancelLegalContractSignatureGQLResult>(
    CANCEL_LEGAL_CONTRACT_SIGNATURE,
  );

  const refetchContracts = useCallback(
    () => apolloClient.refetchQueries({ include: ['getLegalContractsByMission', 'getLegalContractsWithSummary'] }),
    [apolloClient],
  );

  useEffect(() => {
    if (!cancelJobId) return;
    const timeout = setTimeout(() => {
      setCancelJobId(null);
      void refetchContracts();
    }, CANCELLATION_FEEDBACK_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [cancelJobId, refetchContracts]);

  useSubscription<{ jobCompleted: { jobId: string; jobQueueName: string } }>(JOBS_COMPLETED_SUBSCRIPTION, {
    variables: { jobId: cancelJobId },
    skip: !cancelJobId,
    onData: () => {
      setCancelJobId(null);
      void refetchContracts();
      notify('success', t('cancelSuccess'));
    },
  });

  useSubscription<{ jobErrored: { jobId: string; jobQueueName: string; jobError: { message: string } | null } }>(
    JOBS_ERRORED_SUBSCRIPTION,
    {
      variables: { jobId: cancelJobId },
      skip: !cancelJobId,
      onData: (event) => {
        setCancelJobId(null);
        notify('error', event.data.data?.jobErrored.jobError?.message ?? t('cancelError'));
      },
    },
  );
  const typeLabel = t(`voletType.${voletKind}`);
  const typeBadgeColors =
    voletKind === TemplateContract.SST
      ? { color: theme.palette.blue[80], backgroundColor: theme.palette.blue[20] }
      : { color: theme.palette.red[60], backgroundColor: theme.palette.red[20] };
  const signerFullName = resolveSignerDisplayName(resolvedSigner);

  if (!volet) {
    return (
      <Card>
        <HeaderRow>
          <BadgesRow>
            <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
            <StatusChip tone="neutral">{t('badge.notApplicable')}</StatusChip>
          </BadgesRow>
        </HeaderRow>
      </Card>
    );
  }

  const { contract, badgeLabel, badgeTone, isLate } = volet;
  const relaunchDateLabel = contract.lastManualRelaunchAt
    ? dateFormat(contract.lastManualRelaunchAt, 'dd/MM/yyyy')
    : null;
  const sentDateLabel = contract.createdDate ? dateFormat(contract.createdDate, 'dd/MM/yyyy') : null;
  const primaryDateLabel = relaunchDateLabel ?? sentDateLabel;
  const primaryDatePrefix = relaunchDateLabel ? t('relaunchedDatePrefix') : t('sentDatePrefix');
  const signedDateLabel = contract.signedDate ? dateFormat(contract.signedDate, 'dd/MM/yyyy') : null;
  const cancelledDateLabel = contract.cancelledAt ? dateFormat(contract.cancelledAt, 'dd/MM/yyyy') : null;
  const cancelledAuthorLabel = resolveCancellationAuthorLabel(contract.cancelledBy, t);
  // Cancellation history (who / when) lives in the info tip with the reason — keeps the card light.
  // Also hides the misleading "Signé <date>" slot: cancel reuses `signedDate` as its action date.
  const hasCancellationMeta = contract.status === 'cancelled' && Boolean(cancelledDateLabel);
  const hideSignedDateBecauseCancelled = hasCancellationMeta;
  const tipReason = contract.refusedReason?.trim() || null;
  const showCancellationInfoTip = hasCancellationMeta || (contract.status === 'cancelled' && Boolean(tipReason));
  const showRefusedInfoTip = contract.status !== 'cancelled' && Boolean(tipReason);
  const canShowActions = contract.status !== 'pending';
  const relaunchOnCooldown = isManualRelaunchOnCooldown(contract.lastManualRelaunchAt);
  const canRelaunchNow = contract.canRelaunch && !relaunchOnCooldown;
  const cancelInFlight = submittingCancel || Boolean(cancelJobId);

  const handleRelaunch = async () => {
    if (relaunching || !canRelaunchNow) return;
    try {
      const { data } = await relaunchSignature({ variables: { legalContractRecordId: contract.recordId } });
      if (data?.relaunchLegalContractSignature) {
        notify('success', t('relaunchSuccess'));
      }
    } catch (error) {
      notify('error', extractServerErrorMessage(error) ?? t('relaunchError'));
    }
  };

  const handleCancelConfirm = async ({ reason, reasonCategory }: CancelLegalContractSignaturePayload) => {
    if (cancelInFlight) return;
    try {
      const { data } = await cancelSignature({
        variables: {
          legalContractRecordId: contract.recordId,
          reason,
          reasonCategory,
        },
      });
      const jobId = data?.cancelLegalContractSignature?.id;
      if (!jobId) {
        notify('error', t('cancelError'));
        return;
      }
      setCancelJobId(jobId);
      setCancelOpen(false);
      notify('info', t('cancelPending'));
    } catch (error) {
      notify('error', extractServerErrorMessage(error) ?? t('cancelError'));
    }
  };

  const fileActions = canShowActions
    ? buildFileActions({
        contract,
        onClickPreview,
        onClickDownload,
        onUploadOriginal: () => setUploadTarget('original'),
        onUploadSigned: () => setUploadTarget('signed'),
        onRelaunch: handleRelaunch,
        onCancel: () => setCancelOpen(true),
        canRelaunchNow,
        relaunchTooltip: relaunchOnCooldown ? t('relaunchCooldownTooltip') : undefined,
        cancelInFlight,
        cancelTooltip: cancelInFlight ? t('cancelPendingTooltip') : undefined,
        t,
      })
    : [];
  const hasSigner = Boolean(signerFullName);
  const visibleSignedDateLabel = hideSignedDateBecauseCancelled ? null : signedDateLabel;
  const hasDates = Boolean(primaryDateLabel || visibleSignedDateLabel);

  return (
    <Card>
      <HeaderRow>
        <BadgesRow>
          <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
          <StatusChip tone={badgeTone}>
            <StatusIcon tone={badgeTone} />
            {badgeLabel}
          </StatusChip>
          {isLate ? (
            <LateBadge>
              <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
              {t('badge.late')}
            </LateBadge>
          ) : null}
          {showCancellationInfoTip ? (
            <InformationTip>
              <CancellationTipContent>
                {hasCancellationMeta && cancelledDateLabel ? (
                  <CancellationTipMeta>
                    {t('cancelledOnLabel', { date: cancelledDateLabel })}
                    {cancelledAuthorLabel ? (
                      <>
                        {' '}
                        {t('cancelledByPrefix')} <CancellationAuthor>{cancelledAuthorLabel}</CancellationAuthor>
                      </>
                    ) : null}
                  </CancellationTipMeta>
                ) : null}
                {tipReason ? <CancellationTipReason>{tipReason}</CancellationTipReason> : null}
              </CancellationTipContent>
            </InformationTip>
          ) : null}
          {showRefusedInfoTip && tipReason ? <InformationTip>{tipReason}</InformationTip> : null}
        </BadgesRow>
        {fileActions.length > 0 ? (
          <RowActionsMenu
            actions={fileActions}
            label={<MoreVertIcon sx={{ fontSize: 18, opacity: 0.65 }} aria-hidden />}
          />
        ) : null}
      </HeaderRow>

      {hasSigner || hasDates ? (
        <MetaRow>
          {hasSigner && signerFullName ? (
            <SignerSlot>
              <AvatarCell
                initials={initialsFromName(signerFullName)}
                name={signerFullName}
                tone={avatarToneFromSeed(signerFullName)}
              />
            </SignerSlot>
          ) : (
            <span />
          )}
          {hasDates ? (
            <DatesRow>
              {primaryDateLabel ? (
                <span>
                  {primaryDatePrefix} {primaryDateLabel}
                </span>
              ) : null}
              {primaryDateLabel && visibleSignedDateLabel ? <DateSeparator aria-hidden>·</DateSeparator> : null}
              {visibleSignedDateLabel ? (
                <SignedDate>
                  {t('signedDatePrefix')} {visibleSignedDateLabel}
                </SignedDate>
              ) : null}
            </DatesRow>
          ) : null}
        </MetaRow>
      ) : null}

      {canShowActions ? (
        <HiddenUploadHosts>
          {contract.emptyFileUrl ? null : (
            <LegalContractFile
              legalContract={contract}
              signed={false}
              missionRecordId={missionRecordId}
              resolvedSigner={resolvedSigner}
              setUploadingFile={setUploadingFile}
              onClickPreview={onClickPreview}
              hideTrigger
              open={uploadTarget === 'original'}
              onOpenChange={(isOpen) => {
                if (!isOpen) setUploadTarget(null);
              }}
            />
          )}
          {contract.signedFileUrl ? null : (
            <LegalContractFile
              legalContract={contract}
              signed={true}
              missionRecordId={missionRecordId}
              resolvedSigner={resolvedSigner}
              setUploadingFile={setUploadingFile}
              onClickPreview={onClickPreview}
              hideTrigger
              open={uploadTarget === 'signed'}
              onOpenChange={(isOpen) => {
                if (!isOpen) setUploadTarget(null);
              }}
            />
          )}
        </HiddenUploadHosts>
      ) : null}

      <CancelLegalContractSignatureModal
        open={cancelOpen}
        loading={cancelInFlight}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancelConfirm}
      />
    </Card>
  );
};

const StatusIcon = ({ tone }: { tone: DataGridV2Tone }) => {
  const sx = { fontSize: 14 } as const;
  switch (tone) {
    case 'success':
      return <CheckCircleOutlineIcon sx={sx} />;
    case 'warning':
      return <ScheduleOutlinedIcon sx={sx} />;
    case 'danger':
      return <ErrorOutlineIcon sx={sx} />;
    case 'purple':
      return <WarningAmberOutlinedIcon sx={sx} />;
    case 'neutral':
      return <BlockOutlinedIcon sx={sx} />;
    default:
      return null;
  }
};

const buildFileActions = ({
  contract,
  onClickPreview,
  onClickDownload,
  onUploadOriginal,
  onUploadSigned,
  onRelaunch,
  onCancel,
  canRelaunchNow,
  relaunchTooltip,
  cancelInFlight,
  cancelTooltip,
  t,
}: {
  contract: DocumentVolet['contract'];
  onClickPreview: (file: string) => void;
  onClickDownload: (file: string) => void;
  onUploadOriginal: () => void;
  onUploadSigned: () => void;
  onRelaunch: () => void;
  onCancel: () => void;
  canRelaunchNow: boolean;
  relaunchTooltip?: string;
  cancelInFlight: boolean;
  cancelTooltip?: string;
  t: (key: string) => string;
}): DataGridV2RowAction[] => {
  const actions: DataGridV2RowAction[] = [];

  if (contract.emptyFileUrl) {
    actions.push({
      label: t('viewOriginal'),
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: () => onClickPreview(contract.emptyFileUrl as string),
    });
  } else {
    actions.push({
      label: t('uploadOriginal'),
      icon: <UploadFileOutlinedIcon fontSize="small" />,
      onClick: onUploadOriginal,
    });
  }

  if (contract.signedFileUrl) {
    actions.push({
      label: t('downloadSigned'),
      icon: <DownloadOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: () => onClickDownload(contract.signedFileUrl as string),
    });
  } else {
    actions.push({
      label: t('uploadSigned'),
      icon: <UploadFileOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: onUploadSigned,
    });
  }

  // Once signed, the Yousign link is obsolete — keep download / view original only.
  if (contract.signLink && !isContractSigned(contract)) {
    actions.push({
      label: t('openSignDocument'),
      icon: <OpenInNewOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: () => {
        window.open(contract.signLink as string, '_blank', 'noopener,noreferrer');
      },
    });
  }

  // Always listed so the user sees them, greyed out (disabled) when not applicable
  // (status / missing sign link) or when the 24h manual-relaunch cooldown is active.
  actions.push({
    label: t('relaunchSignature'),
    icon: <SendOutlinedIcon fontSize="small" />,
    divider: true,
    disabled: !canRelaunchNow,
    tooltip: relaunchTooltip,
    onClick: onRelaunch,
  });
  actions.push({
    label: t('cancelSignature'),
    icon: <CancelOutlinedIcon fontSize="small" />,
    danger: true,
    divider: true,
    // `canCancel` stays true until the worker converges — also gate on cancelInFlight.
    disabled: !contract.canCancel || cancelInFlight,
    tooltip: cancelTooltip,
    onClick: onCancel,
  });

  return actions;
};

export default DocumentVoletCard;

const Card = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
  width: '100%',
  alignSelf: 'stretch',
  height: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  border: `1px solid ${theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral[10],
  boxSizing: 'border-box',
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0,
}));

const BadgesRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  minWidth: 0,
}));

/** Même rouge que la pastille « X à signer » du header mission (`MissionGroupHeader`). */
const LateBadge = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: '#FCE8E6',
  color: '#C0362C',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const MetaRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minWidth: 0,
  marginTop: 'auto',
}));

const SignerSlot = styled('div')(() => ({
  minWidth: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
}));

const DatesRow = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  marginLeft: 'auto',
  fontSize: 11,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const DateSeparator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
}));

const SignedDate = styled('span')(({ theme }) => ({
  color: theme.palette.green[100],
  fontWeight: 600,
}));

/** Stacks who/when (meta) + reason inside the shared InformationTip popover. */
const CancellationTipContent = styled('span')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 4,
  textAlign: 'left',
}));

/** Secondary line: date + actor — smaller, muted, like card meta dates. */
const CancellationTipMeta = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: 11,
  lineHeight: 1.4,
  fontWeight: 400,
  color: theme.palette.neutral[60],
}));

const CancellationAuthor = styled('strong')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.neutral[80],
}));

/** Primary tip copy: cancellation / refusal reason. */
const CancellationTipReason = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: 13,
  lineHeight: 1.4,
  fontWeight: 500,
  color: theme.palette.neutral[90],
}));

/** Keeps upload modals mounted without occupying layout space. */
const HiddenUploadHosts = styled('div')(() => ({
  display: 'contents',
}));
